import { useEffect, useMemo, useRef, useState } from 'react'
import { MapPin } from 'lucide-react'
import type mapboxgl from 'mapbox-gl'
import type { ExpressionSpecification } from 'mapbox-gl'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import type {
  BlindWithZoneName,
  GeoJsonPolygon,
  MapTapContext,
  ParcelTapContext,
  StandWithZoneName,
  ZoneWithBlindCount,
} from '@/shared/domain'
import { EmptyState } from '@/shared/ui/components'
import { Button, Card } from '@/shared/ui/primitives'
import { env } from '@/shared/config/env'
import { isParcelTilesEnabled } from '@/shared/config/feature-flags'
import { lookupWaypointType } from '@/shared/config/waypoint-types'
import { useAuth, useOrgContext } from '@/shared/ui/hooks'
import { reportError } from '@/shared/infra/observability'
import { parseGpxRoute } from './parse-gpx-route'
import {
  buildParcelBaseLineFilter,
  buildParcelHighlightFilter,
  buildParcelHitFilter,
  buildParcelRevealFilter,
} from './parcel-highlight-filter'
import { buildParcelHighlightFeatureCollection } from './parcel-highlight-geometry'

export type BlindMapSelectedItemType = 'blind' | 'zone' | 'property' | 'spot' | 'scouting' | 'stand'

export type BlindMapCentroid = {
  lat: number
  lng: number
}

export type BlindMapItemClickPayload = {
  id: string
  type: BlindMapSelectedItemType
}

export type BlindMapCanvasClickPayload = {
  lat: number
  lng: number
}

export type BlindMapProps = {
  centroid: BlindMapCentroid | null
  blinds: BlindWithZoneName[]
  zones: ZoneWithBlindCount[]
  stands?: StandWithZoneName[]
  showStands?: boolean
  onStandClick?: (item: { id: string; type: 'stand' }) => void
  showBlinds?: boolean
  showProperties?: boolean
  showZones?: boolean
  showWaypoints?: boolean
  showScouting?: boolean
  showPaths?: boolean
  selectedItemId?: string | null
  selectedItemType?: BlindMapSelectedItemType | null
  onItemClick?: (payload: BlindMapItemClickPayload) => void
  onCanvasClick?: (payload: BlindMapCanvasClickPayload) => void
  canvasClickHasPriority?: boolean
  drawingActive?: boolean
  onPolygonComplete?: (polygon: GeoJSON.Feature<GeoJSON.Polygon>) => void
  mapStyle?: string
  zoom?: number
  height?: string
  emptyStateMessage?: string
  emptyStateActionLabel?: string
  onEmptyStateAction?: () => void
  parcelBoundary?: GeoJsonPolygon | null
  showParcels?: boolean
  pathVertices?: Array<{ lat: number; lng: number }>
  zoneDrawingVertices?: Array<{ lat: number; lng: number }>
  onZoneVertexDragEnd?: (index: number, lat: number, lng: number) => void
  customLocations?: Array<{
    id: string
    latitude: number
    longitude: number
    name?: string
    waypointType?: string | null
    color?: string | null
    category?: string | null
    glyphAsset?: string | null
  }>
  pathFeatures?: Array<{
    id: string
    geometry: Record<string, unknown>
    lineColor?: string | null
    lineStyle?: string | null
    lineWeight?: number | null
  }>
  savedParcels?: Array<{ id: string; geometry: Record<string, unknown>; parcelExternalId?: string | null }>
  propertyBoundaries?: Array<{
    propertyId?: string | null
    geometry: Record<string, unknown>
    lineColor?: string | null
    areaColor?: string | null
    lineStyle?: 'solid' | 'dashed' | 'dotted' | null
    lineWeight?: 1 | 2 | 3 | null
    dissolved?: boolean
  }>
  currentPropertyId?: string | null
  onPropertyBoundaryClick?: (propertyId: string, lat: number, lng: number) => void
  highlightProperty?: {
    propertyId: string
    token: number
    center?: { lat: number; lng: number } | null
  } | null
  cameraTarget?: { lat: number; lng: number; token: number } | null
  searchTapTarget?: { lat: number; lng: number; token: number } | null
  onSearchTapUpgrade?: (ctx: ParcelTapContext) => void
  gpxUrl?: string
  accessToken?: string | null
  onFeatureClick?: (id: string, type: 'scouting' | 'spot' | 'path' | 'parcel') => void
  tapIndicator?: { lat: number; lng: number } | null
  parcelSelectionMode?: boolean
  highlightedRobustIds?: string[]
  ownedParcels?: Array<{
    robustId: string
    lineColor?: string | null
    areaColor?: string | null
    lineStyle?: 'solid' | 'dashed' | 'dotted' | null
    lineWeight?: 1 | 2 | 3 | null
    drawnByPropertyBoundary?: boolean
  }>
  onMapTap?: (ctx: MapTapContext) => void
  onParcelsVisibleChange?: (visible: boolean) => void
  pendingPlacement?: {
    lat: number
    lng: number
    type: 'blind' | 'spot' | 'scouting' | 'stand'
    color?: string | null
    iconName?: string | null
  } | null
  interactive?: boolean
  emphasizeSelection?: boolean
  fitBoundsToken?: number | null
}

const DEFAULT_MAP_STYLE = 'mapbox://styles/mapbox/satellite-streets-v12'
const DEFAULT_ZOOM = 14
const DEFAULT_HEIGHT = '400px'

const ZONE_FILL_OPACITY = 0.15

const ZONE_SELECTED_FILL_OPACITY = 0.35

const ZONE_DEFAULT_LINE_COLOR_HEX = '#4D7FE6'
const ZONE_TRANSPARENT_FILL = 'rgba(0,0,0,0)'

const ZONE_SOURCE_ID = 'blind-map-zones'
const ZONE_FILL_LAYER_ID = 'blind-map-zone-fill'
const ZONE_LINE_SOLID_LAYER_ID = 'blind-map-zone-line-solid'
const ZONE_LINE_DASHED_LAYER_ID = 'blind-map-zone-line-dashed'
const ZONE_LINE_DOTTED_LAYER_ID = 'blind-map-zone-line-dotted'

const PARCEL_SOURCE_ID = 'blind-map-parcel'
const PARCEL_FILL_LAYER_ID = 'blind-map-parcel-fill'
const PARCEL_LINE_LAYER_ID = 'blind-map-parcel-line'

const PATH_DRAWING_SOURCE_ID = 'blind-map-path-drawing'
const PATH_DRAWING_LINE_LAYER_ID = 'blind-map-path-drawing-line'
const PATH_DRAWING_POINTS_SOURCE_ID = 'blind-map-path-drawing-points'
const PATH_DRAWING_POINTS_LAYER_ID = 'blind-map-path-drawing-points-layer'

const ZONE_DRAWING_SOURCE_ID = 'blind-map-zone-drawing'
const ZONE_DRAWING_FILL_LAYER_ID = 'blind-map-zone-drawing-fill'
const ZONE_DRAWING_LINE_LAYER_ID = 'blind-map-zone-drawing-line'
const ZONE_DRAWING_POINTS_SOURCE_ID = 'blind-map-zone-drawing-points'
const ZONE_DRAWING_POINTS_LAYER_ID = 'blind-map-zone-drawing-points-layer'
const ZONE_DRAWING_COLOR = '#4D7FE6'

const SAVED_PATHS_SOURCE_ID = 'blind-map-saved-paths'
const PATH_LINE_SOLID_LAYER_ID = 'blind-map-saved-paths-solid'
const PATH_LINE_DASHED_LAYER_ID = 'blind-map-saved-paths-dashed'
const PATH_LINE_DOTTED_LAYER_ID = 'blind-map-saved-paths-dotted'
const PATH_DEFAULT_LINE_COLOR_HEX = '#9CDB4C'

const SAVED_PARCELS_SOURCE_ID = 'blind-map-saved-parcels'
const SAVED_PARCELS_FILL_LAYER_ID = 'blind-map-saved-parcels-fill'
const SAVED_PARCELS_LINE_LAYER_ID = 'blind-map-saved-parcels-line'

const PROPERTY_BOUNDARY_SOURCE_ID = 'blind-map-property-boundary'
const PROPERTY_BOUNDARY_FILL_LAYER_ID = 'blind-map-property-boundary-fill'
const PROPERTY_BOUNDARY_LINE_SOLID_LAYER_ID = 'blind-map-property-boundary-line-solid'
const PROPERTY_BOUNDARY_LINE_DASHED_LAYER_ID = 'blind-map-property-boundary-line-dashed'
const PROPERTY_BOUNDARY_LINE_DOTTED_LAYER_ID = 'blind-map-property-boundary-line-dotted'
const PROPERTY_BOUNDARY_DEFAULT_LINE_COLOR_HEX = '#4D7FE6'
const PROPERTY_BOUNDARY_FILL_OPACITY = 0.1
const PROPERTY_BOUNDARY_DISSOLVED_FILL_OPACITY = 0.3

const PARCEL_TILE_SOURCE_ID = 'parcel-tile-parcels'
const PARCEL_TILE_LINE_LAYER_ID = 'parcel-tile-line'
const PARCEL_TILE_FILL_HIT_LAYER_ID = 'parcel-tile-fill-hit'
const PARCEL_TILE_LINE_HIGHLIGHT_LAYER_ID = 'parcel-tile-line-highlight'
const PARCEL_HIGHLIGHT_COLOR = '#00B7FF'
const PARCEL_HIGHLIGHT_LINE_WIDTH = 4
const PARCEL_HIGHLIGHT_GEOMETRY_SOURCE_ID = 'blind-map-parcel-highlight'
const PARCEL_HIGHLIGHT_GEOMETRY_LINE_LAYER_ID = 'blind-map-parcel-highlight-line'
const PARCEL_OWNED_FILL_LAYER_ID = 'parcel-owned-fill'
const PARCEL_OWNED_LINE_SOLID_LAYER_ID = 'parcel-owned-line-solid'
const PARCEL_OWNED_LINE_DASHED_LAYER_ID = 'parcel-owned-line-dashed'
const PARCEL_OWNED_LINE_DOTTED_LAYER_ID = 'parcel-owned-line-dotted'
const PARCEL_OWNED_TRANSPARENT = 'rgba(0,0,0,0)'

function ownedLineWidthForWeight(weight: 1 | 2 | 3 | null | undefined): number {
  if (weight === 1) return 1.5
  if (weight === 3) return 4
  return 2.5
}

function removeParcelTileLayersAndSource(map: mapboxgl.Map): void {
  const layerIds = [
    PARCEL_TILE_LINE_LAYER_ID,
    PARCEL_TILE_FILL_HIT_LAYER_ID,
    PARCEL_TILE_LINE_HIGHLIGHT_LAYER_ID,
    PARCEL_OWNED_FILL_LAYER_ID,
    PARCEL_OWNED_LINE_SOLID_LAYER_ID,
    PARCEL_OWNED_LINE_DASHED_LAYER_ID,
    PARCEL_OWNED_LINE_DOTTED_LAYER_ID,
  ]
  for (const id of layerIds) {
    if (map.getLayer(id)) map.removeLayer(id)
  }
  if (map.getSource(PARCEL_TILE_SOURCE_ID)) map.removeSource(PARCEL_TILE_SOURCE_ID)
}

function keepPropertyBoundaryAboveParcelLines(map: mapboxgl.Map): void {
  if (!map.getLayer(PROPERTY_BOUNDARY_LINE_SOLID_LAYER_ID)) return
  for (const layerId of [SAVED_PARCELS_LINE_LAYER_ID, PARCEL_TILE_LINE_LAYER_ID]) {
    if (map.getLayer(layerId)) {
      map.moveLayer(layerId, PROPERTY_BOUNDARY_LINE_SOLID_LAYER_ID)
    }
  }
}

const PARCEL_LINE_COLOR = '#BB8A52'
const PARCEL_LINE_WIDTH = 2
const PARCEL_LINE_OPACITY = 0.7
const PARCEL_TILE_MIN_ZOOM = 12
const PARCEL_TILE_MAX_ZOOM = 17
const PARCEL_TILE_FETCH_MAX_ZOOM = 14

const TAP_INDICATOR_SOURCE_ID = 'tap-indicator-source'
const TAP_INDICATOR_LAYER_ID = 'tap-indicator-layer'

const BLIND_MAP_GPX_SOURCE_ID = 'blind-map-gpx-route'
const BLIND_MAP_GPX_LINE_LAYER_ID = 'blind-map-gpx-route-line'
const BLIND_MAP_GPX_LINE_COLOR = '#F59E0B'
const BLIND_MAP_GPX_LINE_WIDTH = 3

type MapboxModule = typeof mapboxgl

function eachPolygonCoordinate(
  geometry: Record<string, unknown> | undefined,
  visit: (lng: number, lat: number) => void,
): void {
  const g = geometry as { type?: string; coordinates?: unknown } | undefined
  if (!g || !Array.isArray(g.coordinates)) return
  const walkRing = (ring: unknown) => {
    if (!Array.isArray(ring)) return
    for (const coord of ring) {
      if (
        Array.isArray(coord) &&
        coord.length >= 2 &&
        typeof coord[0] === 'number' &&
        typeof coord[1] === 'number'
      ) {
        visit(coord[0], coord[1])
      }
    }
  }
  if (g.type === 'Polygon') {
    for (const ring of g.coordinates) walkRing(ring)
  } else if (g.type === 'MultiPolygon') {
    for (const polygon of g.coordinates) {
      if (Array.isArray(polygon)) for (const ring of polygon) walkRing(ring)
    }
  }
}

function isValidPolygon(boundary: GeoJsonPolygon | undefined): boundary is GeoJsonPolygon {
  return (
    boundary != null &&
    boundary.type === 'Polygon' &&
    Array.isArray(boundary.coordinates) &&
    boundary.coordinates.length > 0
  )
}

function buildZoneFeatureCollection(
  zones: ZoneWithBlindCount[],
  selectedZoneId: string | null,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: zones
      .filter((zone) => isValidPolygon(zone.boundary))
      .map((zone) => ({
        type: 'Feature',
        properties: {
          id: zone.id,
          name: zone.name,
          selected: zone.id === selectedZoneId,
          lineColor: zone.boundaryLineColor ?? null,
          areaColor: zone.boundaryAreaColor ?? null,
          lineStyle: zone.boundaryLineStyle ?? 'solid',
          lineWeight: zone.boundaryLineWeight ?? 2,
        },
        geometry: zone.boundary as GeoJSON.Polygon,
      })),
  }
}

const TEARDROP_DEFAULT_COLOR = '#9CDB4C'
const TEARDROP_WELL_COLOR = '#F5F4E9'
const TEARDROP_GLYPH_DARK = '#3C5A43'

function hexLuminance(hex: string): number {
  const cleaned = hex.replace('#', '')
  if (cleaned.length !== 6) return 0
  const r = parseInt(cleaned.slice(0, 2), 16) / 255
  const g = parseInt(cleaned.slice(2, 4), 16) / 255
  const b = parseInt(cleaned.slice(4, 6), 16) / 255
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function glyphTintForBody(body: string): string {
  return hexLuminance(body) > 0.7 ? TEARDROP_GLYPH_DARK : body
}

function createTeardropMarkerElement(
  isSelected: boolean,
  color?: string | null,
  glyphAsset?: string | null,
  emphasized = false,
): HTMLDivElement {
  const body = color ?? TEARDROP_DEFAULT_COLOR
  const isEmphasized = isSelected && emphasized
  const width = isEmphasized ? 44 : isSelected ? 32 : 28
  const height = Math.round(width * 1.32)

  const el = document.createElement('div')
  el.className = 'blind-map-teardrop'
  el.style.position = 'absolute'
  el.style.width = `${width}px`
  el.style.height = `${height}px`
  el.style.cursor = 'pointer'
  el.style.filter = isEmphasized
    ? 'drop-shadow(0 0 6px rgba(187,138,82,0.95)) drop-shadow(0 0 2px rgba(255,255,255,0.9)) drop-shadow(0 3px 4px rgba(0,0,0,0.45))'
    : isSelected
      ? 'drop-shadow(0 0 3px rgba(187,138,82,0.65)) drop-shadow(0 2px 3px rgba(0,0,0,0.4))'
      : 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))'
  if (isEmphasized) el.style.zIndex = '1'

  el.innerHTML = `<svg width="${width}" height="${height}" viewBox="0 0 28 37" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M14 1C6.82 1 1 6.82 1 14c0 9 13 22 13 22s13-13 13-22C27 6.82 21.18 1 14 1Z" fill="${body}" stroke="${TEARDROP_WELL_COLOR}" stroke-width="2"/><circle cx="14" cy="14" r="7.4" fill="${TEARDROP_WELL_COLOR}"/></svg>`

  if (glyphAsset) {
    const glyph = document.createElement('div')
    const glyphSize = Math.round(width * 0.42)
    glyph.style.position = 'absolute'
    glyph.style.left = '50%'
    glyph.style.top = `${(14 / 37) * 100}%`
    glyph.style.transform = 'translate(-50%, -50%)'
    glyph.style.width = `${glyphSize}px`
    glyph.style.height = `${glyphSize}px`
    glyph.style.backgroundColor = glyphTintForBody(body)
    glyph.style.webkitMaskImage = `url(${glyphAsset})`
    glyph.style.maskImage = `url(${glyphAsset})`
    glyph.style.webkitMaskRepeat = 'no-repeat'
    glyph.style.maskRepeat = 'no-repeat'
    glyph.style.webkitMaskPosition = 'center'
    glyph.style.maskPosition = 'center'
    glyph.style.webkitMaskSize = 'contain'
    glyph.style.maskSize = 'contain'
    el.appendChild(glyph)
  }
  return el
}

function waypointGlyphAsset(iconName?: string | null): string | null {
  return iconName ? `/icons/waypoints/${iconName}.svg` : null
}

const BLIND_GLYPH_ASSET = '/icons/entities/blind.svg'
const STAND_GLYPH_ASSET = '/icons/entities/stand.svg'

export function BlindMap({
  centroid,
  blinds,
  zones,
  stands = [],
  showStands = true,
  onStandClick,
  showBlinds = true,
  showProperties = true,
  showZones = true,
  showWaypoints = true,
  showScouting = true,
  showPaths = true,
  selectedItemId = null,
  selectedItemType = null,
  onItemClick,
  onCanvasClick,
  canvasClickHasPriority,
  drawingActive,
  onPolygonComplete,
  mapStyle = DEFAULT_MAP_STYLE,
  zoom = DEFAULT_ZOOM,
  height = DEFAULT_HEIGHT,
  emptyStateMessage,
  emptyStateActionLabel,
  onEmptyStateAction,
  parcelBoundary,
  showParcels,
  pathVertices,
  zoneDrawingVertices,
  onZoneVertexDragEnd,
  customLocations,
  pathFeatures,
  savedParcels,
  propertyBoundaries,
  currentPropertyId,
  onPropertyBoundaryClick,
  highlightProperty,
  cameraTarget,
  searchTapTarget,
  onSearchTapUpgrade,
  gpxUrl,
  accessToken,
  onFeatureClick,
  tapIndicator = null,
  parcelSelectionMode = false,
  highlightedRobustIds,
  ownedParcels,
  onMapTap,
  onParcelsVisibleChange,
  pendingPlacement,
  interactive = true,
  emphasizeSelection = false,
  fitBoundsToken = null,
}: BlindMapProps) {
  const stableCentroid = useMemo(
    () => centroid,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [centroid?.lat, centroid?.lng],
  )

  const stableCentroidRef = useRef(stableCentroid)
  stableCentroidRef.current = stableCentroid
  const hasCentroid = stableCentroid != null

  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const mapboxRef = useRef<MapboxModule | null>(null)
  const markersRef = useRef<mapboxgl.Marker[]>([])
  const standMarkersRef = useRef<mapboxgl.Marker[]>([])
  const spotMarkersRef = useRef<mapboxgl.Marker[]>([])
  const scoutingLocationsMarkersRef = useRef<mapboxgl.Marker[]>([])
  const centroidMarkerRef = useRef<mapboxgl.Marker | null>(null)
  const pendingMarkerRef = useRef<mapboxgl.Marker | null>(null)
  const zoneVertexMarkersRef = useRef<mapboxgl.Marker[]>([])
  const drawRef = useRef<unknown>(null)
  const reportedParcelStatusesRef = useRef<Set<number>>(new Set())
  const [isMapReady, setIsMapReady] = useState(false)
  const [parcelLayersToken, setParcelLayersToken] = useState(0)
  const [selectedRobustId, setSelectedRobustId] = useState<string | null>(null)
  const selectedRobustIdRef = useRef<string | null>(null)
  useEffect(() => {
    selectedRobustIdRef.current = selectedRobustId
  }, [selectedRobustId])

  const { accessToken: contextAccessToken } = useAuth()
  const effectiveAccessToken = accessToken ?? contextAccessToken
  const accessTokenRef = useRef<string | null>(effectiveAccessToken)
  useEffect(() => {
    accessTokenRef.current = effectiveAccessToken
  }, [effectiveAccessToken])

  const { orgId: activeOrgId, orgType: activeOrgType } = useOrgContext()

  const onItemClickRef = useRef(onItemClick)
  onItemClickRef.current = onItemClick

  const onStandClickRef = useRef(onStandClick)
  onStandClickRef.current = onStandClick

  const onCanvasClickRef = useRef(onCanvasClick)
  onCanvasClickRef.current = onCanvasClick

  const canvasClickHasPriorityRef = useRef(canvasClickHasPriority)
  canvasClickHasPriorityRef.current = canvasClickHasPriority

  const onPolygonCompleteRef = useRef(onPolygonComplete)
  onPolygonCompleteRef.current = onPolygonComplete

  const onFeatureClickRef = useRef(onFeatureClick)
  onFeatureClickRef.current = onFeatureClick

  const onMapTapRef = useRef(onMapTap)
  onMapTapRef.current = onMapTap

  const onPropertyBoundaryClickRef = useRef(onPropertyBoundaryClick)
  onPropertyBoundaryClickRef.current = onPropertyBoundaryClick

  const currentPropertyIdRef = useRef(currentPropertyId)
  currentPropertyIdRef.current = currentPropertyId

  const parcelSelectionModeRef = useRef(parcelSelectionMode)
  parcelSelectionModeRef.current = parcelSelectionMode

  const runTapFlowAt = (
    map: mapboxgl.Map,
    point: mapboxgl.Point,
    lat: number,
    lng: number,
  ): string | null => {
    const parcelLayers = [
      SAVED_PARCELS_FILL_LAYER_ID,
      PARCEL_TILE_FILL_HIT_LAYER_ID,
      PARCEL_TILE_LINE_HIGHLIGHT_LAYER_ID,
    ].filter((id) => map.getLayer(id))
    let robustId: string | null = null
    if (parcelLayers.length > 0) {
      const parcelHits = map.queryRenderedFeatures(point, { layers: parcelLayers })
      robustId = (parcelHits[0]?.properties?.robust_id as string | null) ?? null
    }

    if (parcelSelectionModeRef.current) {
      if (robustId !== null) {
        onMapTapRef.current?.({ kind: 'parcel', robustId, lat, lng })
      }
      return robustId
    }

    let boundaryPropertyId: string | null = null
    if (map.getLayer(PROPERTY_BOUNDARY_FILL_LAYER_ID)) {
      const boundaryHits = map.queryRenderedFeatures(point, {
        layers: [PROPERTY_BOUNDARY_FILL_LAYER_ID],
      })
      boundaryPropertyId =
        (boundaryHits[0]?.properties?.propertyId as string | null | undefined) ?? null
    }

    let zoneId: string | null = null
    if (map.getLayer(ZONE_FILL_LAYER_ID)) {
      const zoneHits = map.queryRenderedFeatures(point, {
        layers: [ZONE_FILL_LAYER_ID],
      })
      zoneId = (zoneHits[0]?.properties?.id as string | null | undefined) ?? null
    }

    if (boundaryPropertyId) {
      if (boundaryPropertyId !== currentPropertyIdRef.current) {
        onPropertyBoundaryClickRef.current?.(boundaryPropertyId, lat, lng)
      }
      if (robustId !== null) {
        setSelectedRobustId(robustId)
        onMapTapRef.current?.({
          kind: 'parcel',
          robustId,
          lat,
          lng,
          propertyId: boundaryPropertyId,
          zoneId,
        })
        return robustId
      }
      onMapTapRef.current?.({
        kind: 'empty',
        lat,
        lng,
        propertyId: boundaryPropertyId,
        zoneId,
      })
      return robustId
    }

    if (robustId !== null) {
      setSelectedRobustId(robustId)
      onMapTapRef.current?.({ kind: 'parcel', robustId, lat, lng })
      return robustId
    }

    if (selectedRobustIdRef.current !== null) {
      setSelectedRobustId(null)
    }
    onMapTapRef.current?.({ kind: 'empty', lat, lng, propertyId: null, zoneId })
    onCanvasClickRef.current?.({ lat, lng })
    return null
  }

  const runTapFlowAtRef = useRef(runTapFlowAt)
  runTapFlowAtRef.current = runTapFlowAt

  const onSearchTapUpgradeRef = useRef(onSearchTapUpgrade)
  onSearchTapUpgradeRef.current = onSearchTapUpgrade

  useEffect(() => {
    const initialCentroid = stableCentroidRef.current
    if (!initialCentroid || !containerRef.current) {
      return
    }

    let cancelled = false
    const container = containerRef.current
    let createdMap: mapboxgl.Map | null = null

    const initMap = async () => {
      const mapboxModule = (await import('mapbox-gl')).default
      if (cancelled) {
        return
      }

      mapboxRef.current = mapboxModule

      const map = new mapboxModule.Map({
        accessToken: env.mapboxToken,
        container,
        style: mapStyle,
        center: [initialCentroid.lng, initialCentroid.lat],
        zoom,
        interactive,
        transformRequest: (url, resourceType) => {
          const token = accessTokenRef.current
          if (resourceType === 'Tile' && url.includes('/functions/v1/parcel-tiles/') && token) {
            return { url, headers: { Authorization: `Bearer ${token}` } }
          }
          return { url }
        },
      })

      createdMap = map
      mapRef.current = map

      map.on('load', () => {
        if (cancelled) {
          return
        }
        map.resize()
        setIsMapReady(true)
      })

      map.on('click', (event) => {
        if (canvasClickHasPriorityRef.current) {
          const handler = onCanvasClickRef.current
          if (handler) handler({ lat: event.lngLat.lat, lng: event.lngLat.lng })
          return
        }

        runTapFlowAtRef.current(map, event.point, event.lngLat.lat, event.lngLat.lng)
      })

      map.on('error', (event) => {
        const sourceId = (event as { sourceId?: string }).sourceId
        if (sourceId !== PARCEL_TILE_SOURCE_ID) return
        const err = (event as { error?: Error & { status?: number; url?: string } }).error
        const status = err?.status
        if (status !== undefined) {
          if (reportedParcelStatusesRef.current.has(status)) return
          reportedParcelStatusesRef.current.add(status)
        }
        reportError(err ?? new Error('Parcel tile request failed'), {
          fallbackMessage: 'Parcel tiles failed to load',
          context: { sourceId, status: status ?? 'unknown', url: err?.url ?? null },
        })
      })
    }

    initMap()

    return () => {
      cancelled = true
      setIsMapReady(false)
      markersRef.current.forEach((marker) => marker.remove())
      markersRef.current = []
      standMarkersRef.current.forEach((marker) => marker.remove())
      standMarkersRef.current = []
      spotMarkersRef.current.forEach((marker) => marker.remove())
      spotMarkersRef.current = []
      scoutingLocationsMarkersRef.current.forEach((marker) => marker.remove())
      scoutingLocationsMarkersRef.current = []
      if (centroidMarkerRef.current) {
        centroidMarkerRef.current.remove()
        centroidMarkerRef.current = null
      }
      if (pendingMarkerRef.current) {
        pendingMarkerRef.current.remove()
        pendingMarkerRef.current = null
      }
      if (drawRef.current && createdMap) {
        createdMap.removeControl(drawRef.current as Parameters<typeof createdMap.removeControl>[0])
        drawRef.current = null
      }
      if (createdMap) {
        if (createdMap.getLayer(TAP_INDICATOR_LAYER_ID)) {
          createdMap.removeLayer(TAP_INDICATOR_LAYER_ID)
        }
        if (createdMap.getSource(TAP_INDICATOR_SOURCE_ID)) {
          createdMap.removeSource(TAP_INDICATOR_SOURCE_ID)
        }
        createdMap.remove()
      }
      mapRef.current = null
      mapboxRef.current = null
    }
  }, [hasCentroid, mapStyle, zoom, interactive])

  useEffect(() => {
    if (centroidMarkerRef.current) {
      centroidMarkerRef.current.remove()
      centroidMarkerRef.current = null
    }
  }, [isMapReady])

  useEffect(() => {
    const map = mapRef.current
    const mapboxModule = mapboxRef.current
    if (!map || !mapboxModule || !isMapReady) {
      return
    }

    markersRef.current.forEach((marker) => marker.remove())
    markersRef.current = []

    if (!showBlinds) {
      return
    }

    blinds.forEach((blind) => {
      if (
        typeof blind.latitude !== 'number' ||
        typeof blind.longitude !== 'number' ||
        Number.isNaN(blind.latitude) ||
        Number.isNaN(blind.longitude)
      ) {
        return
      }

      const isSelected = selectedItemType === 'blind' && selectedItemId === blind.id
      const el = createTeardropMarkerElement(isSelected, blind.color, BLIND_GLYPH_ASSET, emphasizeSelection)
      el.setAttribute('aria-label', `Blind: ${blind.name}`)
      el.addEventListener('click', (event) => {
        event.stopPropagation()
        if (onItemClickRef.current) {
          onItemClickRef.current({ id: blind.id, type: 'blind' })
        }
      })

      const marker = new mapboxModule.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([blind.longitude, blind.latitude])
        .addTo(map)
      markersRef.current.push(marker)
    })
  }, [isMapReady, blinds, showBlinds, selectedItemId, selectedItemType, emphasizeSelection])

  useEffect(() => {
    const map = mapRef.current
    const mapboxModule = mapboxRef.current
    if (!map || !mapboxModule || !isMapReady) {
      return
    }

    standMarkersRef.current.forEach((marker) => marker.remove())
    standMarkersRef.current = []

    if (!showStands) {
      return
    }

    stands.forEach((stand) => {
      if (
        typeof stand.latitude !== 'number' ||
        typeof stand.longitude !== 'number' ||
        Number.isNaN(stand.latitude) ||
        Number.isNaN(stand.longitude)
      ) {
        return
      }

      const isSelected = selectedItemType === 'stand' && selectedItemId === stand.id
      const el = createTeardropMarkerElement(isSelected, stand.color, STAND_GLYPH_ASSET, emphasizeSelection)
      el.setAttribute('aria-label', `Stand: ${stand.name}`)
      el.addEventListener('click', (event) => {
        event.stopPropagation()
        if (onStandClickRef.current) {
          onStandClickRef.current({ id: stand.id, type: 'stand' })
        }
      })

      const marker = new mapboxModule.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([stand.longitude, stand.latitude])
        .addTo(map)
      standMarkersRef.current.push(marker)
    })
  }, [isMapReady, stands, showStands, selectedItemId, selectedItemType, emphasizeSelection])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady) {
      return
    }

    const selectedZoneId = selectedItemType === 'zone' ? selectedItemId : null
    const featureCollection = buildZoneFeatureCollection(zones, selectedZoneId)

    const existingSource = map.getSource(ZONE_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined

    const lineColorExpression: ExpressionSpecification = [
      'coalesce',
      ['get', 'lineColor'],
      ZONE_DEFAULT_LINE_COLOR_HEX,
    ]
    const lineWidthExpression: ExpressionSpecification = [
      'case',
      ['==', ['get', 'selected'], true],
      ['*', ['coalesce', ['get', 'lineWeight'], 2], 1.75],
      ['coalesce', ['get', 'lineWeight'], 2],
    ]

    if (existingSource) {
      existingSource.setData(featureCollection)
    } else {
      map.addSource(ZONE_SOURCE_ID, {
        type: 'geojson',
        data: featureCollection,
      })
      map.addLayer({
        id: ZONE_FILL_LAYER_ID,
        type: 'fill',
        source: ZONE_SOURCE_ID,
        paint: {
          'fill-color': ['coalesce', ['get', 'areaColor'], ZONE_TRANSPARENT_FILL],
          'fill-opacity': [
            'case',
            ['==', ['get', 'selected'], true],
            ZONE_SELECTED_FILL_OPACITY,
            ZONE_FILL_OPACITY,
          ],
        },
      })
      map.addLayer({
        id: ZONE_LINE_SOLID_LAYER_ID,
        type: 'line',
        source: ZONE_SOURCE_ID,
        filter: ['==', ['get', 'lineStyle'], 'solid'],
        paint: {
          'line-color': lineColorExpression,
          'line-width': lineWidthExpression,
        },
      })
      map.addLayer({
        id: ZONE_LINE_DASHED_LAYER_ID,
        type: 'line',
        source: ZONE_SOURCE_ID,
        filter: ['==', ['get', 'lineStyle'], 'dashed'],
        paint: {
          'line-color': lineColorExpression,
          'line-width': lineWidthExpression,
          'line-dasharray': [4, 3],
        },
      })
      map.addLayer({
        id: ZONE_LINE_DOTTED_LAYER_ID,
        type: 'line',
        source: ZONE_SOURCE_ID,
        filter: ['==', ['get', 'lineStyle'], 'dotted'],
        paint: {
          'line-color': lineColorExpression,
          'line-width': lineWidthExpression,
          'line-dasharray': [1, 2],
        },
      })
      map.on('click', ZONE_FILL_LAYER_ID, (event) => {
        if (canvasClickHasPriorityRef.current) return
        const feature = event.features?.[0]
        const zoneId = feature?.properties?.id as string | undefined
        if (zoneId && onItemClickRef.current) {
          onItemClickRef.current({ id: zoneId, type: 'zone' })
        }
      })
    }

    const visibility = showZones ? 'visible' : 'none'
    const lineLayerIds = [
      ZONE_LINE_SOLID_LAYER_ID,
      ZONE_LINE_DASHED_LAYER_ID,
      ZONE_LINE_DOTTED_LAYER_ID,
    ]
    if (map.getLayer(ZONE_FILL_LAYER_ID)) {
      map.setLayoutProperty(ZONE_FILL_LAYER_ID, 'visibility', visibility)
    }
    for (const id of lineLayerIds) {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', visibility)
      }
    }
  }, [isMapReady, zones, showZones, selectedItemId, selectedItemType])

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return
    const map = mapRef.current

    if (
      parcelBoundary &&
      parcelBoundary.type === 'Polygon' &&
      parcelBoundary.coordinates.length > 0
    ) {
      const featureCollection: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: parcelBoundary as GeoJSON.Polygon,
          },
        ],
      }
      const existingSource = map.getSource(PARCEL_SOURCE_ID) as
        | mapboxgl.GeoJSONSource
        | undefined
      if (existingSource) {
        existingSource.setData(featureCollection)
      } else {
        map.addSource(PARCEL_SOURCE_ID, {
          type: 'geojson',
          data: featureCollection,
        })
        map.addLayer({
          id: PARCEL_FILL_LAYER_ID,
          type: 'fill',
          source: PARCEL_SOURCE_ID,
          paint: {
            'fill-color': '#8B5CF6',
            'fill-opacity': 0.2,
          },
        })
        map.addLayer({
          id: PARCEL_LINE_LAYER_ID,
          type: 'line',
          source: PARCEL_SOURCE_ID,
          paint: {
            'line-color': '#8B5CF6',
            'line-width': 2,
          },
        })
      }
      const visibility = showProperties !== false ? 'visible' : 'none'
      if (map.getLayer(PARCEL_FILL_LAYER_ID)) {
        map.setLayoutProperty(PARCEL_FILL_LAYER_ID, 'visibility', visibility)
      }
      if (map.getLayer(PARCEL_LINE_LAYER_ID)) {
        map.setLayoutProperty(PARCEL_LINE_LAYER_ID, 'visibility', visibility)
      }
    } else {
      const existingSource = map.getSource(PARCEL_SOURCE_ID) as
        | mapboxgl.GeoJSONSource
        | undefined
      if (existingSource) {
        existingSource.setData({ type: 'FeatureCollection', features: [] })
      }
    }
  }, [isMapReady, parcelBoundary, showProperties])

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return
    const map = mapRef.current

    const emptyCollection: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

    const lineData: GeoJSON.FeatureCollection =
      pathVertices && pathVertices.length >= 2
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: pathVertices.map((v) => [v.lng, v.lat]),
                },
              },
            ],
          }
        : emptyCollection

    const pointData: GeoJSON.FeatureCollection =
      pathVertices && pathVertices.length > 0
        ? {
            type: 'FeatureCollection',
            features: pathVertices.map((v) => ({
              type: 'Feature' as const,
              properties: {},
              geometry: { type: 'Point' as const, coordinates: [v.lng, v.lat] },
            })),
          }
        : emptyCollection

    const existingLineSource = map.getSource(PATH_DRAWING_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (existingLineSource) {
      existingLineSource.setData(lineData)
    } else {
      map.addSource(PATH_DRAWING_SOURCE_ID, { type: 'geojson', data: lineData })
      map.addLayer({
        id: PATH_DRAWING_LINE_LAYER_ID,
        type: 'line',
        source: PATH_DRAWING_SOURCE_ID,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#10B981', 'line-width': 3, 'line-dasharray': [2, 2] },
      })
    }

    const existingPointSource = map.getSource(PATH_DRAWING_POINTS_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (existingPointSource) {
      existingPointSource.setData(pointData)
    } else {
      map.addSource(PATH_DRAWING_POINTS_SOURCE_ID, { type: 'geojson', data: pointData })
      map.addLayer({
        id: PATH_DRAWING_POINTS_LAYER_ID,
        type: 'circle',
        source: PATH_DRAWING_POINTS_SOURCE_ID,
        paint: { 'circle-color': '#10B981', 'circle-radius': 5 },
      })
    }
  }, [isMapReady, pathVertices])

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return
    const map = mapRef.current
    const emptyCollection: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }
    const vertices = zoneDrawingVertices ?? []

    const ringCoords =
      vertices.length >= 3
        ? [...vertices.map((v) => [v.lng, v.lat]), [vertices[0].lng, vertices[0].lat]]
        : vertices.map((v) => [v.lng, v.lat])

    const lineData: GeoJSON.FeatureCollection =
      vertices.length >= 2
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'LineString', coordinates: ringCoords },
              },
            ],
          }
        : emptyCollection

    const fillData: GeoJSON.FeatureCollection =
      vertices.length >= 3
        ? {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'Polygon', coordinates: [ringCoords] },
              },
            ],
          }
        : emptyCollection

    const verticesAreDraggable = typeof onZoneVertexDragEnd === 'function'
    const pointData: GeoJSON.FeatureCollection =
      !verticesAreDraggable && vertices.length > 0
        ? {
            type: 'FeatureCollection',
            features: vertices.map((v) => ({
              type: 'Feature' as const,
              properties: {},
              geometry: { type: 'Point' as const, coordinates: [v.lng, v.lat] },
            })),
          }
        : emptyCollection

    const existingFillSource = map.getSource(ZONE_DRAWING_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined
    if (existingFillSource) {
      existingFillSource.setData(fillData)
    } else {
      map.addSource(ZONE_DRAWING_SOURCE_ID, { type: 'geojson', data: fillData })
      map.addLayer({
        id: ZONE_DRAWING_FILL_LAYER_ID,
        type: 'fill',
        source: ZONE_DRAWING_SOURCE_ID,
        paint: { 'fill-color': ZONE_DRAWING_COLOR, 'fill-opacity': 0.15 },
      })
    }

    const lineSourceId = `${ZONE_DRAWING_SOURCE_ID}-line`
    const existingLineSource = map.getSource(lineSourceId) as mapboxgl.GeoJSONSource | undefined
    if (existingLineSource) {
      existingLineSource.setData(lineData)
    } else {
      map.addSource(lineSourceId, { type: 'geojson', data: lineData })
      map.addLayer({
        id: ZONE_DRAWING_LINE_LAYER_ID,
        type: 'line',
        source: lineSourceId,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ZONE_DRAWING_COLOR, 'line-width': 2.5 },
      })
    }

    const existingPointSource = map.getSource(ZONE_DRAWING_POINTS_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined
    if (existingPointSource) {
      existingPointSource.setData(pointData)
    } else {
      map.addSource(ZONE_DRAWING_POINTS_SOURCE_ID, { type: 'geojson', data: pointData })
      map.addLayer({
        id: ZONE_DRAWING_POINTS_LAYER_ID,
        type: 'circle',
        source: ZONE_DRAWING_POINTS_SOURCE_ID,
        paint: {
          'circle-color': '#FFFFFF',
          'circle-radius': 5,
          'circle-stroke-color': ZONE_DRAWING_COLOR,
          'circle-stroke-width': 2,
        },
      })
    }
  }, [isMapReady, zoneDrawingVertices, onZoneVertexDragEnd])

  useEffect(() => {
    const map = mapRef.current
    const mapboxModule = mapboxRef.current
    if (!map || !mapboxModule || !isMapReady) return

    zoneVertexMarkersRef.current.forEach((marker) => marker.remove())
    zoneVertexMarkersRef.current = []

    const onDragEnd = onZoneVertexDragEnd
    const vertices = zoneDrawingVertices ?? []
    if (!onDragEnd || vertices.length === 0) return

    const lineSourceId = `${ZONE_DRAWING_SOURCE_ID}-line`
    const emptyCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    }

    const updateShapeFromMarkers = () => {
      const points = zoneVertexMarkersRef.current.map((marker) => {
        const lngLat = marker.getLngLat()
        return [lngLat.lng, lngLat.lat]
      })
      const ring = points.length >= 3 ? [...points, points[0]] : points
      const lineSource = map.getSource(lineSourceId) as mapboxgl.GeoJSONSource | undefined
      if (lineSource) {
        lineSource.setData(
          points.length >= 2
            ? {
                type: 'FeatureCollection',
                features: [
                  { type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: ring } },
                ],
              }
            : emptyCollection,
        )
      }
      const fillSource = map.getSource(ZONE_DRAWING_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
      if (fillSource) {
        fillSource.setData(
          points.length >= 3
            ? {
                type: 'FeatureCollection',
                features: [
                  { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } },
                ],
              }
            : emptyCollection,
        )
      }
    }

    vertices.forEach((vertex, index) => {
      const el = document.createElement('div')
      el.style.width = '14px'
      el.style.height = '14px'
      el.style.borderRadius = '9999px'
      el.style.backgroundColor = '#FFFFFF'
      el.style.border = `2px solid ${ZONE_DRAWING_COLOR}`
      el.style.boxShadow = '0 1px 2px rgba(0,0,0,0.35)'
      el.style.cursor = 'grab'
      const marker = new mapboxModule.Marker({ element: el, anchor: 'center', draggable: true })
        .setLngLat([vertex.lng, vertex.lat])
        .addTo(map)
      marker.on('drag', updateShapeFromMarkers)
      marker.on('dragend', () => {
        const lngLat = marker.getLngLat()
        onDragEnd(index, lngLat.lat, lngLat.lng)
      })
      zoneVertexMarkersRef.current.push(marker)
    })

    return () => {
      zoneVertexMarkersRef.current.forEach((marker) => marker.remove())
      zoneVertexMarkersRef.current = []
    }
  }, [isMapReady, zoneDrawingVertices, onZoneVertexDragEnd])

  useEffect(() => {
    const map = mapRef.current
    const mapboxModule = mapboxRef.current
    if (!map || !mapboxModule || !isMapReady) return

    spotMarkersRef.current.forEach((marker) => marker.remove())
    spotMarkersRef.current = []

    if (!showWaypoints || !customLocations || customLocations.length === 0) {
      return
    }

    customLocations
      .filter((loc) => loc.category !== 'scouting')
      .forEach((loc) => {
        if (typeof loc.latitude !== 'number' || typeof loc.longitude !== 'number') {
          return
        }
        const isSelected = selectedItemType === 'spot' && selectedItemId === loc.id
        const iconName = loc.waypointType ? lookupWaypointType(loc.waypointType).iconName : null
        const glyphAsset = loc.glyphAsset ?? waypointGlyphAsset(iconName)
        const el = createTeardropMarkerElement(isSelected, loc.color, glyphAsset, emphasizeSelection)
        el.setAttribute('aria-label', `Waypoint: ${loc.name ?? 'waypoint'}`)
        el.addEventListener('click', (event) => {
          event.stopPropagation()
          if (onFeatureClickRef.current) {
            onFeatureClickRef.current(loc.id, 'spot')
          }
        })
        const marker = new mapboxModule.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([loc.longitude, loc.latitude])
          .addTo(map)
        spotMarkersRef.current.push(marker)
      })
  }, [isMapReady, customLocations, showWaypoints, selectedItemId, selectedItemType, emphasizeSelection])

  useEffect(() => {
    const map = mapRef.current
    const mapboxModule = mapboxRef.current
    if (!map || !mapboxModule || !isMapReady) return

    scoutingLocationsMarkersRef.current.forEach((marker) => marker.remove())
    scoutingLocationsMarkersRef.current = []

    if (!showScouting || !customLocations || customLocations.length === 0) {
      return
    }

    customLocations
      .filter((loc) => loc.category === 'scouting')
      .forEach((loc) => {
        if (typeof loc.latitude !== 'number' || typeof loc.longitude !== 'number') {
          return
        }
        const isSelected = selectedItemType === 'scouting' && selectedItemId === loc.id
        const iconName = loc.waypointType ? lookupWaypointType(loc.waypointType).iconName : null
        const el = createTeardropMarkerElement(isSelected, loc.color, waypointGlyphAsset(iconName), emphasizeSelection)
        el.setAttribute('aria-label', `Scouting: ${loc.name ?? 'pin'}`)
        el.addEventListener('click', (event) => {
          event.stopPropagation()
          if (onFeatureClickRef.current) {
            onFeatureClickRef.current(loc.id, 'spot')
          }
        })
        const marker = new mapboxModule.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([loc.longitude, loc.latitude])
          .addTo(map)
        scoutingLocationsMarkersRef.current.push(marker)
      })
  }, [isMapReady, customLocations, showScouting, selectedItemId, selectedItemType, emphasizeSelection])

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return
    const map = mapRef.current
    const emptyCollection: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

    const featureCollection: GeoJSON.FeatureCollection =
      pathFeatures && pathFeatures.length > 0
        ? {
            type: 'FeatureCollection',
            features: pathFeatures
              .filter(
                (f) =>
                  f.geometry &&
                  (f.geometry as { type?: string }).type === 'LineString' &&
                  Array.isArray((f.geometry as { coordinates?: unknown }).coordinates),
              )
              .map((f) => ({
                type: 'Feature' as const,
                properties: {
                  id: f.id,
                  lineStyle: f.lineStyle ?? 'solid',
                  lineColor: f.lineColor ?? null,
                  lineWeight: f.lineWeight ?? 2,
                },
                geometry: f.geometry as unknown as GeoJSON.LineString,
              })),
          }
        : emptyCollection

    const pathLineColorExpression: ExpressionSpecification = [
      'coalesce',
      ['get', 'lineColor'],
      PATH_DEFAULT_LINE_COLOR_HEX,
    ]
    const pathLineWidthExpression: ExpressionSpecification = [
      'coalesce',
      ['get', 'lineWeight'],
      2,
    ]

    const existingSource = map.getSource(SAVED_PATHS_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (existingSource) {
      existingSource.setData(featureCollection)
    } else {
      map.addSource(SAVED_PATHS_SOURCE_ID, { type: 'geojson', data: featureCollection })
      map.addLayer({
        id: PATH_LINE_SOLID_LAYER_ID,
        type: 'line',
        source: SAVED_PATHS_SOURCE_ID,
        filter: ['==', ['get', 'lineStyle'], 'solid'],
        paint: {
          'line-color': pathLineColorExpression,
          'line-width': pathLineWidthExpression,
        },
      })
      map.addLayer({
        id: PATH_LINE_DASHED_LAYER_ID,
        type: 'line',
        source: SAVED_PATHS_SOURCE_ID,
        filter: ['==', ['get', 'lineStyle'], 'dashed'],
        paint: {
          'line-color': pathLineColorExpression,
          'line-width': pathLineWidthExpression,
          'line-dasharray': [4, 3],
        },
      })
      map.addLayer({
        id: PATH_LINE_DOTTED_LAYER_ID,
        type: 'line',
        source: SAVED_PATHS_SOURCE_ID,
        filter: ['==', ['get', 'lineStyle'], 'dotted'],
        paint: {
          'line-color': pathLineColorExpression,
          'line-width': pathLineWidthExpression,
          'line-dasharray': [1, 2],
        },
      })
      const pathLayerIds = [
        PATH_LINE_SOLID_LAYER_ID,
        PATH_LINE_DASHED_LAYER_ID,
        PATH_LINE_DOTTED_LAYER_ID,
      ]
      for (const layerId of pathLayerIds) {
        map.on('click', layerId, (e) => {
          const feature = e.features?.[0]
          if (feature?.properties?.id && onFeatureClickRef.current) {
            onFeatureClickRef.current(feature.properties.id as string, 'path')
          }
        })
      }
    }

    const visibility = showPaths ? 'visible' : 'none'
    const pathVisibilityLayerIds = [
      PATH_LINE_SOLID_LAYER_ID,
      PATH_LINE_DASHED_LAYER_ID,
      PATH_LINE_DOTTED_LAYER_ID,
    ]
    for (const layerId of pathVisibilityLayerIds) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility)
      }
    }
  }, [isMapReady, pathFeatures, showPaths])

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return
    const map = mapRef.current
    const emptyCollection: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

    const featureCollection: GeoJSON.FeatureCollection =
      savedParcels && savedParcels.length > 0
        ? {
            type: 'FeatureCollection',
            features: savedParcels
              .filter((f) => {
                const geomType = (f.geometry as { type?: string } | undefined)?.type
                return (
                  (geomType === 'Polygon' || geomType === 'MultiPolygon') &&
                  Array.isArray((f.geometry as { coordinates?: unknown }).coordinates)
                )
              })
              .map((f) => ({
                type: 'Feature' as const,
                properties: { id: f.id, robust_id: f.parcelExternalId ?? null },
                geometry: f.geometry as unknown as GeoJSON.Polygon | GeoJSON.MultiPolygon,
              })),
          }
        : emptyCollection

    const existingSource = map.getSource(SAVED_PARCELS_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (existingSource) {
      existingSource.setData(featureCollection)
    } else {
      map.addSource(SAVED_PARCELS_SOURCE_ID, { type: 'geojson', data: featureCollection })
      map.addLayer({
        id: SAVED_PARCELS_FILL_LAYER_ID,
        type: 'fill',
        source: SAVED_PARCELS_SOURCE_ID,
        paint: {
          'fill-color': '#000000',
          'fill-opacity': 0,
        },
      })
      map.addLayer({
        id: SAVED_PARCELS_LINE_LAYER_ID,
        type: 'line',
        source: SAVED_PARCELS_SOURCE_ID,
        paint: {
          'line-color': PARCEL_LINE_COLOR,
          'line-width': PARCEL_LINE_WIDTH,
          'line-opacity': PARCEL_LINE_OPACITY,
        },
      })
      keepPropertyBoundaryAboveParcelLines(map)
    }

    const visibility = showProperties !== false ? 'visible' : 'none'
    if (map.getLayer(SAVED_PARCELS_FILL_LAYER_ID)) {
      map.setLayoutProperty(SAVED_PARCELS_FILL_LAYER_ID, 'visibility', visibility)
    }
    if (map.getLayer(SAVED_PARCELS_LINE_LAYER_ID)) {
      map.setLayoutProperty(SAVED_PARCELS_LINE_LAYER_ID, 'visibility', visibility)
    }
  }, [isMapReady, savedParcels, showProperties])

  const propertyBoundariesKey = JSON.stringify(propertyBoundaries ?? [])

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return
    const map = mapRef.current

    const isValidGeometry = (
      g: { type?: string; coordinates?: unknown } | undefined,
    ): boolean =>
      g != null &&
      (g.type === 'Polygon' || g.type === 'MultiPolygon') &&
      Array.isArray(g.coordinates) &&
      g.coordinates.length > 0

    const boundaryFeatures: GeoJSON.Feature[] = (propertyBoundaries ?? [])
      .filter((b) => isValidGeometry(b.geometry as { type?: string; coordinates?: unknown }))
      .map((b) => ({
        type: 'Feature',
        properties: {
          propertyId: b.propertyId ?? null,
          lineColor: b.lineColor ?? null,
          areaColor: b.areaColor ?? null,
          lineStyle: b.lineStyle ?? 'solid',
          lineWeight: b.lineWeight ?? 2,
          dissolved: b.dissolved ?? false,
        },
        geometry: b.geometry as unknown as GeoJSON.Polygon | GeoJSON.MultiPolygon,
      }))

    const featureCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: boundaryFeatures,
    }

    const lineColorExpression: ExpressionSpecification = [
      'coalesce',
      ['get', 'lineColor'],
      PROPERTY_BOUNDARY_DEFAULT_LINE_COLOR_HEX,
    ]
    const lineWidthExpression: ExpressionSpecification = [
      'coalesce',
      ['get', 'lineWeight'],
      2,
    ]

    const existingSource = map.getSource(PROPERTY_BOUNDARY_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined
    if (existingSource) {
      existingSource.setData(featureCollection)
      return
    }

    map.addSource(PROPERTY_BOUNDARY_SOURCE_ID, { type: 'geojson', data: featureCollection })
    map.addLayer({
      id: PROPERTY_BOUNDARY_FILL_LAYER_ID,
      type: 'fill',
      source: PROPERTY_BOUNDARY_SOURCE_ID,
      paint: {
        'fill-color': ['coalesce', ['get', 'areaColor'], 'rgba(0,0,0,0)'],
        'fill-opacity': [
          'case',
          ['==', ['get', 'dissolved'], true],
          PROPERTY_BOUNDARY_DISSOLVED_FILL_OPACITY,
          PROPERTY_BOUNDARY_FILL_OPACITY,
        ],
      },
    })
    map.addLayer({
      id: PROPERTY_BOUNDARY_LINE_SOLID_LAYER_ID,
      type: 'line',
      source: PROPERTY_BOUNDARY_SOURCE_ID,
      filter: ['==', ['get', 'lineStyle'], 'solid'],
      paint: {
        'line-color': lineColorExpression,
        'line-width': lineWidthExpression,
      },
    })
    map.addLayer({
      id: PROPERTY_BOUNDARY_LINE_DASHED_LAYER_ID,
      type: 'line',
      source: PROPERTY_BOUNDARY_SOURCE_ID,
      filter: ['==', ['get', 'lineStyle'], 'dashed'],
      paint: {
        'line-color': lineColorExpression,
        'line-width': lineWidthExpression,
        'line-dasharray': [4, 3],
      },
    })
    map.addLayer({
      id: PROPERTY_BOUNDARY_LINE_DOTTED_LAYER_ID,
      type: 'line',
      source: PROPERTY_BOUNDARY_SOURCE_ID,
      filter: ['==', ['get', 'lineStyle'], 'dotted'],
      paint: {
        'line-color': lineColorExpression,
        'line-width': lineWidthExpression,
        'line-dasharray': [1, 2],
      },
    })
    keepPropertyBoundaryAboveParcelLines(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady, propertyBoundariesKey])

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return
    const map = mapRef.current
    const visibility = showProperties !== false ? 'visible' : 'none'
    for (const id of [
      PROPERTY_BOUNDARY_FILL_LAYER_ID,
      PROPERTY_BOUNDARY_LINE_SOLID_LAYER_ID,
      PROPERTY_BOUNDARY_LINE_DASHED_LAYER_ID,
      PROPERTY_BOUNDARY_LINE_DOTTED_LAYER_ID,
    ]) {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', visibility)
      }
    }
  }, [isMapReady, showProperties])

  const highlightToken = highlightProperty?.token ?? null

  useEffect(() => {
    const map = mapRef.current
    const mapboxModule = mapboxRef.current
    if (!map || !mapboxModule || !isMapReady) return
    const targetPropertyId = highlightProperty?.propertyId
    if (!targetPropertyId) return

    const bounds = new mapboxModule.LngLatBounds()
    let hasCoordinate = false
    for (const boundary of propertyBoundaries ?? []) {
      if ((boundary.propertyId ?? null) !== targetPropertyId) continue
      eachPolygonCoordinate(boundary.geometry, (lng, lat) => {
        bounds.extend([lng, lat])
        hasCoordinate = true
      })
    }
    if (hasCoordinate && !bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 48, duration: 600, maxZoom: 16 })
      return
    }

    const center = highlightProperty?.center
    if (center) {
      map.flyTo({
        center: [center.lng, center.lat],
        duration: 600,
        zoom: Math.max(map.getZoom(), 15),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightToken, isMapReady])

  useEffect(() => {
    const map = mapRef.current
    const mapboxModule = mapboxRef.current
    if (!map || !mapboxModule || !isMapReady || fitBoundsToken == null) return
    const bounds = new mapboxModule.LngLatBounds()
    let hasCoordinate = false
    for (const zone of zones) {
      if (!isValidPolygon(zone.boundary)) continue
      eachPolygonCoordinate(zone.boundary, (lng, lat) => {
        bounds.extend([lng, lat])
        hasCoordinate = true
      })
    }
    if (hasCoordinate && !bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 48, duration: 0, maxZoom: 18 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitBoundsToken, isMapReady])

  const cameraTargetToken = cameraTarget?.token ?? null

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady || !cameraTarget) return
    map.flyTo({
      center: [cameraTarget.lng, cameraTarget.lat],
      duration: 600,
      zoom: Math.max(map.getZoom(), 15),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraTargetToken, isMapReady])

  const searchTapTargetToken = searchTapTarget?.token ?? null

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady || !searchTapTarget) return
    const { lat, lng } = searchTapTarget

    map.flyTo({
      center: [lng, lat],
      duration: 600,
      zoom: Math.max(map.getZoom(), 15),
    })

    let settled = false

    const recheck = () => {
      if (settled) return
      settled = true
      map.off('click', cancel)
      if (canvasClickHasPriorityRef.current) return
      const point = map.project([lng, lat])
      const parcelLayers = [
        SAVED_PARCELS_FILL_LAYER_ID,
        PARCEL_TILE_FILL_HIT_LAYER_ID,
        PARCEL_TILE_LINE_HIGHLIGHT_LAYER_ID,
      ].filter((id) => map.getLayer(id))
      if (parcelLayers.length === 0) return
      const parcelHits = map.queryRenderedFeatures(point, { layers: parcelLayers })
      const foundRobustId = (parcelHits[0]?.properties?.robust_id as string | null) ?? null
      if (foundRobustId === null) return
      if (map.getLayer(PROPERTY_BOUNDARY_FILL_LAYER_ID)) {
        const boundaryHits = map.queryRenderedFeatures(point, {
          layers: [PROPERTY_BOUNDARY_FILL_LAYER_ID],
        })
        const boundaryPropertyId =
          (boundaryHits[0]?.properties?.propertyId as string | null | undefined) ?? null
        if (boundaryPropertyId !== null) return
      }
      onSearchTapUpgradeRef.current?.({ kind: 'parcel', robustId: foundRobustId, lat, lng })
    }

    const cancel = () => {
      if (settled) return
      settled = true
      map.off('idle', recheck)
      map.off('click', cancel)
    }

    const handleMoveEnd = () => {
      if (canvasClickHasPriorityRef.current) return
      const point = map.project([lng, lat])
      const robustId = runTapFlowAtRef.current(map, point, lat, lng)
      if (robustId !== null) return
      const parcelLayers = [
        SAVED_PARCELS_FILL_LAYER_ID,
        PARCEL_TILE_FILL_HIT_LAYER_ID,
        PARCEL_TILE_LINE_HIGHLIGHT_LAYER_ID,
      ].filter((id) => map.getLayer(id))
      if (parcelLayers.length === 0) return
      map.once('idle', recheck)
      map.on('click', cancel)
    }

    map.once('moveend', handleMoveEnd)

    return () => {
      map.off('moveend', handleMoveEnd)
      if (!settled) {
        settled = true
        map.off('idle', recheck)
        map.off('click', cancel)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTapTargetToken, isMapReady])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady) return
    if (!isParcelTilesEnabled()) return

    removeParcelTileLayersAndSource(map)

    if (!effectiveAccessToken) return
    if (!activeOrgId || !activeOrgType) return

    const parcelTileQuery = new URLSearchParams()
    parcelTileQuery.set('rev', '6')
    parcelTileQuery.set('orgId', activeOrgId)
    parcelTileQuery.set('orgType', activeOrgType)
    const parcelTileUrl = `${env.edgeFunctionsBaseUrl}/parcel-tiles/{z}/{x}/{y}?${parcelTileQuery.toString()}`

    map.addSource(PARCEL_TILE_SOURCE_ID, {
      type: 'vector',
      tiles: [parcelTileUrl],
      minzoom: PARCEL_TILE_MIN_ZOOM,
      maxzoom: PARCEL_TILE_FETCH_MAX_ZOOM,
      promoteId: { parcels: 'robust_id' },
    })

    map.addLayer({
      id: PARCEL_TILE_LINE_LAYER_ID,
      type: 'line',
      source: PARCEL_TILE_SOURCE_ID,
      'source-layer': 'parcels',
      minzoom: PARCEL_TILE_MIN_ZOOM,
      maxzoom: PARCEL_TILE_MAX_ZOOM,
      filter: buildParcelRevealFilter() as ExpressionSpecification,
      paint: {
        'line-color': PARCEL_LINE_COLOR,
        'line-width': PARCEL_LINE_WIDTH,
        'line-opacity': PARCEL_LINE_OPACITY,
      },
    })

    map.addLayer({
      id: PARCEL_TILE_FILL_HIT_LAYER_ID,
      type: 'fill',
      source: PARCEL_TILE_SOURCE_ID,
      'source-layer': 'parcels',
      filter: buildParcelRevealFilter() as ExpressionSpecification,
      paint: {
        'fill-color': '#000000',
        'fill-opacity': 0,
      },
    })

    map.addLayer({
      id: PARCEL_TILE_LINE_HIGHLIGHT_LAYER_ID,
      type: 'line',
      source: PARCEL_TILE_SOURCE_ID,
      'source-layer': 'parcels',
      filter: buildParcelHighlightFilter(null),
      paint: {
        'line-color': PARCEL_HIGHLIGHT_COLOR,
        'line-width': PARCEL_HIGHLIGHT_LINE_WIDTH,
        'line-opacity': 1,
      },
    })

    map.addLayer({
      id: PARCEL_OWNED_FILL_LAYER_ID,
      type: 'fill',
      source: PARCEL_TILE_SOURCE_ID,
      'source-layer': 'parcels',
      paint: { 'fill-color': PARCEL_OWNED_TRANSPARENT, 'fill-opacity': 0.3 },
    })
    map.addLayer({
      id: PARCEL_OWNED_LINE_SOLID_LAYER_ID,
      type: 'line',
      source: PARCEL_TILE_SOURCE_ID,
      'source-layer': 'parcels',
      filter: ['in', ['get', 'robust_id'], ['literal', []]],
      paint: { 'line-color': PARCEL_OWNED_TRANSPARENT, 'line-width': 2.5 },
    })
    map.addLayer({
      id: PARCEL_OWNED_LINE_DASHED_LAYER_ID,
      type: 'line',
      source: PARCEL_TILE_SOURCE_ID,
      'source-layer': 'parcels',
      filter: ['in', ['get', 'robust_id'], ['literal', []]],
      paint: { 'line-color': PARCEL_OWNED_TRANSPARENT, 'line-width': 2.5, 'line-dasharray': [4, 3] },
    })
    map.addLayer({
      id: PARCEL_OWNED_LINE_DOTTED_LAYER_ID,
      type: 'line',
      source: PARCEL_TILE_SOURCE_ID,
      'source-layer': 'parcels',
      filter: ['in', ['get', 'robust_id'], ['literal', []]],
      paint: { 'line-color': PARCEL_OWNED_TRANSPARENT, 'line-width': 2.5, 'line-dasharray': [1, 2] },
    })

    keepPropertyBoundaryAboveParcelLines(map)
    setParcelLayersToken((v) => v + 1)
  }, [isMapReady, activeOrgId, activeOrgType, effectiveAccessToken])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady) return
    const visibility = showParcels !== false ? 'visible' : 'none'
    for (const layerId of [
      PARCEL_TILE_LINE_LAYER_ID,
      PARCEL_TILE_FILL_HIT_LAYER_ID,
      PARCEL_TILE_LINE_HIGHLIGHT_LAYER_ID,
      PARCEL_OWNED_FILL_LAYER_ID,
      PARCEL_OWNED_LINE_SOLID_LAYER_ID,
      PARCEL_OWNED_LINE_DASHED_LAYER_ID,
      PARCEL_OWNED_LINE_DOTTED_LAYER_ID,
    ]) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility)
      }
    }
  }, [isMapReady, showParcels, parcelLayersToken])

  const highlightedRobustIdsKey = (highlightedRobustIds ?? []).join(',')
  const ownedParcelsKey = JSON.stringify(ownedParcels ?? [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady) return
    if (!map.getLayer(PARCEL_OWNED_FILL_LAYER_ID)) return
    const owned = ownedParcels ?? []
    const matchNone: ExpressionSpecification = ['in', ['get', 'robust_id'], ['literal', []]]
    const ownedIds = owned.map((o) => o.robustId)
    const tileStyled = owned.filter((o) => o.drawnByPropertyBoundary !== true)
    const tileStyledIds = tileStyled.map((o) => o.robustId)

    map.setFilter(
      PARCEL_TILE_LINE_LAYER_ID,
      buildParcelBaseLineFilter(tileStyledIds) as ExpressionSpecification,
    )
    if (map.getLayer(PARCEL_TILE_FILL_HIT_LAYER_ID)) {
      map.setFilter(
        PARCEL_TILE_FILL_HIT_LAYER_ID,
        buildParcelHitFilter(ownedIds) as ExpressionSpecification,
      )
    }

    if (tileStyled.length === 0) {
      map.setPaintProperty(PARCEL_OWNED_FILL_LAYER_ID, 'fill-color', PARCEL_OWNED_TRANSPARENT)
    } else {
      const fillMatch: unknown[] = ['match', ['get', 'robust_id']]
      for (const o of tileStyled) fillMatch.push(o.robustId, o.areaColor ?? PARCEL_OWNED_TRANSPARENT)
      fillMatch.push(PARCEL_OWNED_TRANSPARENT)
      map.setPaintProperty(
        PARCEL_OWNED_FILL_LAYER_ID,
        'fill-color',
        fillMatch as unknown as ExpressionSpecification,
      )
    }

    const styleLayers: Array<['solid' | 'dashed' | 'dotted', string]> = [
      ['solid', PARCEL_OWNED_LINE_SOLID_LAYER_ID],
      ['dashed', PARCEL_OWNED_LINE_DASHED_LAYER_ID],
      ['dotted', PARCEL_OWNED_LINE_DOTTED_LAYER_ID],
    ]
    for (const [style, layerId] of styleLayers) {
      const group = tileStyled.filter((o) => (o.lineStyle ?? 'solid') === style)
      const ids = group.map((o) => o.robustId)
      map.setFilter(
        layerId,
        ids.length === 0
          ? matchNone
          : (['in', ['get', 'robust_id'], ['literal', ids]] as ExpressionSpecification),
      )
      if (ids.length === 0) continue
      const colorMatch: unknown[] = ['match', ['get', 'robust_id']]
      const widthMatch: unknown[] = ['match', ['get', 'robust_id']]
      for (const o of group) {
        colorMatch.push(o.robustId, o.lineColor ?? PARCEL_OWNED_TRANSPARENT)
        widthMatch.push(o.robustId, ownedLineWidthForWeight(o.lineWeight))
      }
      colorMatch.push(PARCEL_OWNED_TRANSPARENT)
      widthMatch.push(2.5)
      map.setPaintProperty(layerId, 'line-color', colorMatch as unknown as ExpressionSpecification)
      map.setPaintProperty(layerId, 'line-width', widthMatch as unknown as ExpressionSpecification)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady, ownedParcelsKey, parcelLayersToken])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady) return
    if (!map.getLayer(PARCEL_TILE_LINE_HIGHLIGHT_LAYER_ID)) return
    const ids = new Set<string>(highlightedRobustIds ?? [])
    if (selectedRobustId !== null) ids.add(selectedRobustId)
    map.setFilter(
      PARCEL_TILE_LINE_HIGHLIGHT_LAYER_ID,
      buildParcelHighlightFilter(Array.from(ids)),
    )
  }, [isMapReady, selectedRobustId, highlightedRobustIdsKey, highlightedRobustIds, parcelLayersToken])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady) return
    const ids = highlightedRobustIds ?? []
    const featureCollection = buildParcelHighlightFeatureCollection(
      savedParcels ?? [],
      selectedRobustId === null ? ids : [...ids, selectedRobustId],
    )
    const visibility = showParcels !== false ? 'visible' : 'none'

    const existingSource = map.getSource(PARCEL_HIGHLIGHT_GEOMETRY_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined
    if (existingSource) {
      existingSource.setData(featureCollection)
    } else {
      map.addSource(PARCEL_HIGHLIGHT_GEOMETRY_SOURCE_ID, {
        type: 'geojson',
        data: featureCollection,
      })
      map.addLayer({
        id: PARCEL_HIGHLIGHT_GEOMETRY_LINE_LAYER_ID,
        type: 'line',
        source: PARCEL_HIGHLIGHT_GEOMETRY_SOURCE_ID,
        paint: {
          'line-color': PARCEL_HIGHLIGHT_COLOR,
          'line-width': PARCEL_HIGHLIGHT_LINE_WIDTH,
          'line-opacity': 1,
        },
      })
    }
    if (map.getLayer(PARCEL_HIGHLIGHT_GEOMETRY_LINE_LAYER_ID)) {
      map.setLayoutProperty(PARCEL_HIGHLIGHT_GEOMETRY_LINE_LAYER_ID, 'visibility', visibility)
    }
  }, [isMapReady, selectedRobustId, highlightedRobustIds, savedParcels, showParcels])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady || !onParcelsVisibleChange) return
    const emit = () => {
      onParcelsVisibleChange(
        showParcels !== false && map.getZoom() >= PARCEL_TILE_MIN_ZOOM,
      )
    }
    emit()
    map.on('zoomend', emit)
    return () => {
      map.off('zoomend', emit)
    }
  }, [isMapReady, showParcels, onParcelsVisibleChange])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !isMapReady) return

    const emptyCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    }

    const featureCollection: GeoJSON.FeatureCollection = tapIndicator
      ? {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'Point',
                coordinates: [tapIndicator.lng, tapIndicator.lat],
              },
            },
          ],
        }
      : emptyCollection

    const existingSource = map.getSource(TAP_INDICATOR_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined

    if (existingSource) {
      existingSource.setData(featureCollection)
      return
    }

    map.addSource(TAP_INDICATOR_SOURCE_ID, {
      type: 'geojson',
      data: featureCollection,
    })
    map.addLayer({
      id: TAP_INDICATOR_LAYER_ID,
      type: 'circle',
      source: TAP_INDICATOR_SOURCE_ID,
      paint: {
        'circle-radius': 7,
        'circle-color': '#9A7243',
        'circle-stroke-width': 2,
        'circle-stroke-color': '#FFFFFF',
      },
    })
  }, [isMapReady, tapIndicator])

  useEffect(() => {
    if (tapIndicator) return
    setSelectedRobustId(null)
  }, [tapIndicator])

  const pendingPlacementKey = pendingPlacement
    ? `${pendingPlacement.type}|${pendingPlacement.lat}|${pendingPlacement.lng}|${pendingPlacement.color ?? ''}|${pendingPlacement.iconName ?? ''}`
    : null

  useEffect(() => {
    const map = mapRef.current
    const mapboxModule = mapboxRef.current
    if (!map || !mapboxModule || !isMapReady) return

    if (pendingMarkerRef.current) {
      pendingMarkerRef.current.remove()
      pendingMarkerRef.current = null
    }

    if (!pendingPlacement) return

    const glyphAsset =
      pendingPlacement.type === 'blind'
        ? BLIND_GLYPH_ASSET
        : pendingPlacement.type === 'stand'
          ? STAND_GLYPH_ASSET
          : waypointGlyphAsset(pendingPlacement.iconName)
    const el = createTeardropMarkerElement(true, pendingPlacement.color, glyphAsset)
    el.style.pointerEvents = 'none'
    pendingMarkerRef.current = new mapboxModule.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([pendingPlacement.lng, pendingPlacement.lat])
      .addTo(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady, pendingPlacementKey])

  useEffect(() => {
    if (!isMapReady || !mapRef.current) return
    const map = mapRef.current
    let cancelled = false

    const emptyCollection: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

    if (!gpxUrl) {
      const existingSource = map.getSource(BLIND_MAP_GPX_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
      if (existingSource) {
        existingSource.setData(emptyCollection)
      }
      return
    }

    ;(async () => {
      try {
        const response = await fetch(gpxUrl)
        if (cancelled) return
        if (!response.ok) {
          reportError(new Error(`GPX fetch failed: ${response.status}`), {
            fallbackMessage: 'Failed to load GPX route',
          })
          return
        }
        const text = await response.text()
        if (cancelled) return
        const points = parseGpxRoute(text)
        if (cancelled) return
        if (!points) {
          reportError(new Error('GPX parse returned no route points'), {
            fallbackMessage: 'Failed to parse GPX route',
          })
          return
        }
        const featureCollection: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: points.map((p) => [p.lng, p.lat]),
              },
            },
          ],
        }
        const existingSource = map.getSource(BLIND_MAP_GPX_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
        if (existingSource) {
          existingSource.setData(featureCollection)
        } else {
          map.addSource(BLIND_MAP_GPX_SOURCE_ID, { type: 'geojson', data: featureCollection })
          map.addLayer({
            id: BLIND_MAP_GPX_LINE_LAYER_ID,
            type: 'line',
            source: BLIND_MAP_GPX_SOURCE_ID,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': BLIND_MAP_GPX_LINE_COLOR,
              'line-width': BLIND_MAP_GPX_LINE_WIDTH,
            },
          })
        }
      } catch (err) {
        if (cancelled) return
        reportError(err, { fallbackMessage: 'Failed to load GPX route' })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isMapReady, gpxUrl])

  useEffect(() => {
    if (!drawingActive || !isMapReady || !mapRef.current) return

    let cancelled = false
    const map = mapRef.current
    const handleCreateRef: { current: ((e: { features: GeoJSON.Feature[] }) => void) | null } = { current: null }

    ;(async () => {
      const MapboxDraw = (await import('@mapbox/mapbox-gl-draw')).default
      const snapModule = await import('mapbox-gl-draw-snap-mode')
      if (cancelled || !mapRef.current) return

      const { SnapPolygonMode, SnapLineMode, SnapPointMode } = snapModule

      const drawOptions = {
        displayControlsDefault: false,
        controls: { polygon: true, trash: true },
        modes: {
          ...MapboxDraw.modes,
          draw_polygon: SnapPolygonMode,
          draw_line_string: SnapLineMode,
          draw_point: SnapPointMode,
        },
        snap: true,
        snapOptions: {
          snapPx: 12,
          snapToMidPoints: false,
          snapVertexPriorityDistance: 0.0025,
        },
        guides: false,
        userProperties: true,
        styles: [
          { id: 'gl-draw-polygon-fill-inactive', type: 'fill', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']], paint: { 'fill-color': '#3bb2d0', 'fill-outline-color': '#3bb2d0', 'fill-opacity': 0.1 } },
          { id: 'gl-draw-polygon-fill-active', type: 'fill', filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']], paint: { 'fill-color': '#fbb03b', 'fill-outline-color': '#fbb03b', 'fill-opacity': 0.1 } },
          { id: 'gl-draw-polygon-midpoint', type: 'circle', filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']], paint: { 'circle-radius': 3, 'circle-color': '#fbb03b' } },
          { id: 'gl-draw-polygon-stroke-inactive', type: 'line', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#3bb2d0', 'line-width': 2 } },
          { id: 'gl-draw-polygon-stroke-active', type: 'line', filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#fbb03b', 'line-width': 2 } },
          { id: 'gl-draw-line-inactive', type: 'line', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString'], ['!=', 'mode', 'static']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#3bb2d0', 'line-width': 2 } },
          { id: 'gl-draw-line-active', type: 'line', filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#fbb03b', 'line-width': 2 } },
          { id: 'gl-draw-polygon-and-line-vertex-stroke-inactive', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']], paint: { 'circle-radius': 5, 'circle-color': '#ffffff' } },
          { id: 'gl-draw-polygon-and-line-vertex-inactive', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']], paint: { 'circle-radius': 3, 'circle-color': '#fbb03b' } },
          { id: 'gl-draw-point-point-stroke-inactive', type: 'circle', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Point'], ['==', 'meta', 'feature'], ['!=', 'mode', 'static']], paint: { 'circle-radius': 5, 'circle-opacity': 1, 'circle-color': '#ffffff' } },
          { id: 'gl-draw-point-inactive', type: 'circle', filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Point'], ['==', 'meta', 'feature'], ['!=', 'mode', 'static']], paint: { 'circle-radius': 3, 'circle-color': '#3bb2d0' } },
          { id: 'gl-draw-point-stroke-active', type: 'circle', filter: ['all', ['==', '$type', 'Point'], ['==', 'active', 'true'], ['!=', 'meta', 'midpoint']], paint: { 'circle-radius': 7, 'circle-color': '#ffffff' } },
          { id: 'gl-draw-point-active', type: 'circle', filter: ['all', ['==', '$type', 'Point'], ['!=', 'meta', 'midpoint'], ['==', 'active', 'true']], paint: { 'circle-radius': 5, 'circle-color': '#fbb03b' } },
          { id: 'gl-draw-polygon-fill-static', type: 'fill', filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']], paint: { 'fill-color': '#404040', 'fill-outline-color': '#404040', 'fill-opacity': 0.1 } },
          { id: 'gl-draw-polygon-stroke-static', type: 'line', filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#404040', 'line-width': 2 } },
          { id: 'gl-draw-line-static', type: 'line', filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'LineString']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#404040', 'line-width': 2 } },
          { id: 'gl-draw-point-static', type: 'circle', filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Point']], paint: { 'circle-radius': 5, 'circle-color': '#404040' } },
        ],
      }
      const draw = new MapboxDraw(drawOptions as ConstructorParameters<typeof MapboxDraw>[0])
      drawRef.current = draw
      map.addControl(draw as unknown as Parameters<typeof map.addControl>[0])
      draw.changeMode('draw_polygon')

      const handleCreate = (e: { features: GeoJSON.Feature[] }) => {
        if (e.features[0]) {
          onPolygonCompleteRef.current?.(e.features[0] as GeoJSON.Feature<GeoJSON.Polygon>)
        }
      }
      handleCreateRef.current = handleCreate
      map.on('draw.create', handleCreate)
    })()

    return () => {
      cancelled = true
      if (handleCreateRef.current) {
        map.off('draw.create', handleCreateRef.current)
      }
      if (drawRef.current) {
        map.removeControl(drawRef.current as Parameters<typeof map.removeControl>[0])
        drawRef.current = null
      }
    }
  }, [drawingActive, isMapReady])

  const heightStyle = height === '100%' ? undefined : { height }
  const heightClass = height === '100%' ? 'h-full' : ''

  if (!centroid) {
    const showAction = Boolean(emptyStateActionLabel && onEmptyStateAction)
    return (
      <Card className={`overflow-hidden p-0 ${heightClass}`} style={heightStyle}>
        <div className="flex h-full w-full items-center justify-center p-6">
          <EmptyState
            icon={MapPin}
            heading="No map location"
            description={emptyStateMessage ?? 'No property centroid set'}
            action={
              showAction ? (
                <Button onClick={onEmptyStateAction}>{emptyStateActionLabel}</Button>
              ) : undefined
            }
          />
        </div>
      </Card>
    )
  }

  return (
    <Card className={`relative overflow-hidden p-0 ${heightClass}`} style={heightStyle}>
      <div
        ref={containerRef}
        className="absolute inset-0"
        data-testid="blind-map-container"
      />
    </Card>
  )
}
